import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { Slide, QuestionSlide, AnswerRecord, AnswerEntry } from "../types";

export const useQuizStore = defineStore("quiz", () => {
    const slides = ref<Slide[]>([]);
    const currentIndex = ref(0);

    const score = ref(0);
    const answers = ref<AnswerRecord>({});

    const siteUrl = ref("");

    function setSiteUrl(u: string) {
        siteUrl.value = u;
    }

    const appReady = ref(false);
    function setAppReady(v: boolean) {
        appReady.value = v;
    }

    const started = ref(false);

    const START_KEY = "quiz_started";

    function loadStarted() {
        started.value = sessionStorage.getItem(START_KEY) === "1";
    }

    function setStarted(v: boolean) {
        started.value = v;
        if (v) sessionStorage.setItem(START_KEY, "1");
        else sessionStorage.removeItem(START_KEY);
    }

    function resetQuiz() {
        score.value = 0;
        answers.value = {};
        justAnsweredId.value = null;
        visited.value = new Set();
        setStarted(false);
    }

    const currentSlide = computed(() => slides.value[currentIndex.value]);

    const questions = computed(
        () =>
            slides.value.filter(
                (s) => s.kind === "question",
            ) as QuestionSlide[],
    );

    const totalQuestions = computed(() => questions.value.length);

    function loadSlides(list: Slide[]) {
        slides.value = list;
        currentIndex.value = 0;
    }

    function findIndexByRoute(route: string): number {
        return slides.value.findIndex((s) => s.route === route);
    }

    function goToRoute(route: string) {
        const idx = findIndexByRoute(route);
        if (idx >= 0) currentIndex.value = idx;
    }

    function getPrevNavigableRoute() {
        const currentRoute = currentSlide.value?.route;

        for (let i = currentIndex.value; i >= 2; i--) {
            const s = slides.value[i];
            console.log("s=", s);
            if (!s) continue;
            if (s.route === currentRoute) continue;
            return s.route;
        }
        return null;
    }

    function getQuestionById(id: string): QuestionSlide | undefined {
        return questions.value.find((q) => q.questionId === id);
    }

    function getQuestionIndex(id: string): number {
        return questions.value.findIndex((q) => q.questionId === id);
    }

    function getNextQuestionId(currentQuestionId: string): string | null {
        const idx = getQuestionIndex(currentQuestionId);
        if (idx < 0) return null;
        const next = questions.value[idx + 1];
        return next ? next.questionId : null;
    }

    function getFirstQuestionId(): string | null {
        return questions.value[0]?.questionId ?? null;
    }

    function getAnswer(questionId: string): AnswerEntry | undefined {
        return answers.value[questionId];
    }

    function isAnswered(questionId: string): boolean {
        return answers.value[questionId] !== undefined;
    }

    const justAnsweredId = ref<string | null>(null);

    function answerQuestion(question: QuestionSlide, selectedIndex: number) {
        if (answers.value[question.questionId]) return;

        const choice = question.choices[selectedIndex];
        const isCorrect = !!choice?.isCorrect;

        if (isCorrect) score.value += 1;

        answers.value = {
            ...answers.value,
            [question.questionId]: { selectedIndex, isCorrect },
        };

        justAnsweredId.value = question.questionId;
    }

    function clearJustAnswered() {
        justAnsweredId.value = null;
    }

    function shouldShowRevisitControls(questionId: string): boolean {
        return isAnswered(questionId) && justAnsweredId.value !== questionId;
    }

    // visited tracking (for “only animate first visit”)
    const visited = ref<Set<string>>(new Set());

    function hasVisited(id: string): boolean {
        return visited.value.has(id);
    }

    function enterSlide(id: string): boolean {
        const firstVisit = !visited.value.has(id);
        visited.value.add(id);
        return firstVisit;
    }

    const navHistory = ref<string[]>([]);

    function pushHistory(path: string) {
        const last = navHistory.value[navHistory.value.length - 1];
        if (last === path) return;
        navHistory.value.push(path);
    }

    function popHistory(): string | null {
        navHistory.value.pop(); // remove current
        return navHistory.value.pop() ?? null; // return previous
    }

    // Share modal state (keep)
    const isShareModal = ref(false);
    function openShare() {
        isShareModal.value = true;
    }
    function closeShare() {
        isShareModal.value = false;
    }

    return {
        siteUrl,
        setSiteUrl,

        appReady,
        setAppReady,

        started,
        loadStarted,
        setStarted,
        resetQuiz,

        slides,
        currentSlide,
        currentIndex,

        score,
        answers,

        questions,
        totalQuestions,

        loadSlides,
        goToRoute,
        getPrevNavigableRoute,
        getQuestionById,
        getQuestionIndex,
        getNextQuestionId,
        getFirstQuestionId,

        answerQuestion,
        getAnswer,
        isAnswered,

        justAnsweredId,
        clearJustAnswered,
        shouldShowRevisitControls,

        hasVisited,
        enterSlide,

        isShareModal,
        openShare,
        closeShare,

        navHistory,
        pushHistory,
        popHistory,
    };
});
